import React from 'react';

import {
  MoneyFormat,
  OfferDescriptionWrapper,
  OfferPrice,
  PriceBeforeDiscount,
  Title,
} from '../elements';
import stripHtml from '../../utils/strip-html';
import { Offer, Product, ProductVariant } from '../../types';
import { ProductOfferPricing } from '../ProductOfferPricing';
import OfferDescriptionContent from '../OfferBody/tree/OfferDescriptionContent';
import Badge from '../OfferBody/Badge';
import { getIntelligemsMinProductPrice } from '../../shared/product-page-placement/getIntelligemsMinProductPrice';
import {
  getIntelligemsComparePriceByVariantId,
  getIntelligemsVariantPrice,
} from '../../shared/product-page-placement/intelligemsUtils';
import { getManualProductDescription } from '../../utils/offer-utils';
import { useAppsInfo } from '../../contexts/AppsInfoContext';

interface Props {
  productOrServiceOffer: Product | Offer; // Tech debt: can be a product or an offer (in service path)
  offer: Offer;
  variant?: ProductVariant;
  quantity: number;
}

const IGNORED_VARIANT_TITLE = 'Default Title';

export const TitleSection = ({
  productOrServiceOffer,
  variant,
  quantity,
  offer,
}: Props) => {
  const { id, title, headline, price } = productOrServiceOffer as any;
  const appsInfo = useAppsInfo();

  const serviceVariantId = !offer.is_product
    ? offer.items?.[0]?.item_variants?.[0]
    : undefined;
  let intelligemsPrice: number | undefined;
  if (offer.is_product) {
    intelligemsPrice = getIntelligemsMinProductPrice(id, appsInfo);
  } else {
    intelligemsPrice = getIntelligemsVariantPrice(
      appsInfo.is_intelligems_enabled,
      serviceVariantId,
      `Service Offer: ${headline}`,
    );
  }

  const intelligemsComparePrice = getIntelligemsComparePriceByVariantId(
    appsInfo.is_intelligems_enabled,
    serviceVariantId,
    `Service Offer Compare: ${title || headline}`,
  );

  const renderVariantTitle = () =>
    offer.is_product &&
    variant?.title &&
    variant.title !== IGNORED_VARIANT_TITLE
      ? ` - ${variant.title}`
      : '';

  const productDescription = getManualProductDescription(
    offer,
    (productOrServiceOffer as any)?.handle,
  );
  const serviceOfferShortDescription =
    !offer.is_product && offer.short_description
      ? offer.short_description
      : null;
  const descriptionToShow =
    serviceOfferShortDescription?.trim() || productDescription?.trim();

  return (
    <>
      <Badge offer={offer} id={productOrServiceOffer.id} />
      <Title>{stripHtml(`${title || headline} ${renderVariantTitle()}`)}</Title>
      {variant ? (
        <ProductOfferPricing
          variant={variant}
          offer={offer}
          quantity={quantity}
        />
      ) : (
        <OfferPrice>
          <MoneyFormat
            amount={
              /* Since Intelligems has only 1 variant, the getIntelligemsMinProductPrice returns the same for variant and for minPrice */
              intelligemsPrice
                ? (Number(intelligemsPrice) / 100) * quantity
                : Number(price) *
                  quantity *
                  Number(window.Shopify.currency.rate)
            }
          />
          {
            /* CR doesn't offer discount for services, so compare price is only relevant for Intelligems */
            intelligemsComparePrice !== undefined &&
              intelligemsComparePrice !== null &&
              intelligemsPrice !== undefined &&
              intelligemsPrice !== null &&
              Number(intelligemsComparePrice) > Number(intelligemsPrice) && (
                <PriceBeforeDiscount>
                  <MoneyFormat amount={Number(intelligemsComparePrice) / 100} />
                </PriceBeforeDiscount>
              )
          }
        </OfferPrice>
      )}
      {descriptionToShow && (
        <OfferDescriptionWrapper>
          <OfferDescriptionContent>{descriptionToShow}</OfferDescriptionContent>
        </OfferDescriptionWrapper>
      )}
    </>
  );
};
